import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { TEMPLATE_CONFIGS } from '../../config/templateConfig';
import type { TemplateType } from '../../types/template';
import { type ValidationResult, validateDocxTemplate } from '../../utils/templateValidation';
import TemplatePreviewDialog from './TemplatePreviewDialog';

interface TemplateUploaderProps {
  type: TemplateType;
  onUpload: (file: File, validTags: string[], scope: 'personal' | 'company') => Promise<void>;
  canManageClient?: boolean;
}

const TemplateUploader: React.FC<TemplateUploaderProps> = ({
  type,
  onUpload,
  canManageClient = false,
}) => {
  const theme = useTheme();
  const [file, setFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [scope, setScope] = useState<'personal' | 'company'>('personal');

  // Reset state when type changes
  useEffect(() => {
    setFile(null);
    setValidationResult(null);
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const uploadedFile = acceptedFiles[0];
      if (!uploadedFile) {
        return;
      }

      const config = TEMPLATE_CONFIGS[type];
      if (!config) {
        return;
      }

      setFile(uploadedFile);
      setIsValidating(true);
      setValidationResult(null);

      const result = await validateDocxTemplate(uploadedFile, config);
      setValidationResult(result);
      setIsValidating(false);
    },
    [type]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const handleConfirmUpload = async () => {
    if (!file || !validationResult?.isValid) {
      return;
    }
    setIsUploading(true);
    try {
      await onUpload(file, validationResult.foundTags, scope);
      // Reset state after successful upload
      setFile(null);
      setValidationResult(null);
    } catch {
      // Error handled by parent onUpload function
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setFile(null);
    setValidationResult(null);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        border: `1px solid ${theme.palette.greyContrast}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Upload New Template</Typography>
      </Box>

      {!file ? (
        <Box
          {...getRootProps()}
          sx={{
            border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.grey[400]}`,
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? theme.palette.action.hover : 'transparent',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2 }} />
          <Typography variant="h6" color="text.primary" gutterBottom>
            {isDragActive ? 'Drop the file here...' : 'Drag & drop your .docx template here'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            or click to select file
          </Typography>
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
              {file.name}
            </Typography>
            <Button onClick={handleCancel} disabled={isUploading}>
              Change File
            </Button>
          </Box>

          {canManageClient && (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="template-scope-label">Visibility</InputLabel>
                <Select
                  labelId="template-scope-label"
                  value={scope}
                  label="Visibility"
                  onChange={(e) => setScope(e.target.value as 'personal' | 'company')}
                  disabled={isUploading}
                >
                  <MenuItem value="personal">Personal Template (Only visible to you)</MenuItem>
                  <MenuItem value="company">Company Template (Available to all users)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {isValidating && <CircularProgress size={24} />}

          {!isValidating && validationResult && (
            <Box sx={{ mb: 2 }}>
              {validationResult.isValid ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  Template is valid! All required tags found.
                </Alert>
              ) : (
                <Alert severity="error" icon={<ErrorIcon />}>
                  Validation Failed. Missing required tags:
                  <List dense>
                    {validationResult.missingTags.map((tag) => (
                      <ListItem key={tag}>
                        <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                          <ErrorIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText primary={tag} />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}

              {/* Warning for duplicates */}
              {validationResult.duplicateTags.length > 0 && (
                <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
                  Duplicate tags detected (this might be intentional):
                  <List dense>
                    {validationResult.duplicateTags.map((tag) => (
                      <ListItem key={tag}>
                        <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
                          <WarningAmberIcon fontSize="small" color="warning" />
                        </ListItemIcon>
                        <ListItemText primary={tag} />
                      </ListItem>
                    ))}
                  </List>
                </Alert>
              )}

              {/* Optional: Show found tags for confirmation */}
              {validationResult.foundTags.length > 0 && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'transparent', borderRadius: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    Tags detected:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {validationResult.foundTags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleCancel} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => setShowPreview(true)}
              disabled={isUploading || isValidating}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmUpload}
              disabled={!validationResult?.isValid || isUploading}
              startIcon={isUploading && <CircularProgress size={20} color="inherit" />}
            >
              {isUploading ? 'Uploading...' : 'Upload Template'}
            </Button>
          </Box>
        </Box>
      )}

      <TemplatePreviewDialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        file={file}
        fileName={file ? file.name : ''}
        templateType={type}
      />
    </Paper>
  );
};

export default TemplateUploader;
